import { useEffect, useState } from "react";
import styled from "styled-components";
import { SizedBox } from "../../../common/components/CommonBaseComponents";
import CustomGraphCard from "../../../common/components/card/CustomGraphCard";
import { useWindowSize } from "../../../common/hooks/useWindowSize";
import Translate from "../../../common/translate/Translate";
import RevenueService from "../../../service/RevenueService";
import { format, subDays, subMonths } from "date-fns";

const WebGraphGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 17px;
    height: 426px;
`;

// 사용량: day API(04.01.05.10) 는 day, week API(04.01.05.09) 는 dayWeek 로 기간 구분
function mapUsageToGraphData(data) {
    return (Array.isArray(data) ? data : []).map((item) => ({
        name: item.day ?? item.dayWeek ?? "",
        Washer: item.washer ?? 0,
        Dryer: item.dryer ?? 0,
        Combo: item.combo ?? 0,
    }));
}

// 그래프 데이터(Washer/Dryer/Combo)의 전체 합계 — 상단 합계 숫자와 막대 값이 항상 일치하게 함
function sumGraphData(data) {
    return (data ?? []).reduce((sum, item) => sum + (item.Washer || 0) + (item.Dryer || 0) + (item.Combo || 0), 0);
}

// 매출: revenue/day(04.01.05.01) — { day, total, washer, dryer, combo }
function mapRevenueToGraphData(data) {
    return (Array.isArray(data) ? data : []).map((item) => ({
        name: item.day ?? "",
        Washer: item.washer ?? 0,
        Dryer: item.dryer ?? 0,
        Combo: item.combo ?? 0,
    }));
}

function DashboardGraphArea() {
    const { width } = useWindowSize();
    const revenueService = new RevenueService();

    const [usageDailyData, setUsageDailyData] = useState([]);
    const [usageWeeklyData, setUsageWeeklyData] = useState([]);
    const [usageMonthlyData, setUsageMonthlyData] = useState([]);

    const [revenueDailyData, setRevenueDailyData] = useState([]);
    const [revenueWeeklyData, setRevenueWeeklyData] = useState([]);
    const [revenueMonthlyData, setRevenueMonthlyData] = useState([]);

    // 카드 내부 기간 탭(Daily/Weekly/Monthly)을 받아 상단 합계 숫자를 탭에 맞춰 표시
    const [usageTab, setUsageTab] = useState("Daily");
    const [revenueTab, setRevenueTab] = useState("Daily");

    const pickByTab = (tab, daily, weekly, monthly) => {
        if (tab === "Weekly") return weekly;
        if (tab === "Monthly") return monthly;
        return daily;
    };

    const totalTransactions = sumGraphData(
        pickByTab(usageTab, usageDailyData, usageWeeklyData, usageMonthlyData)
    ).toLocaleString();

    const totalRevenue = `฿${sumGraphData(
        pickByTab(revenueTab, revenueDailyData, revenueWeeklyData, revenueMonthlyData)
    ).toLocaleString()}`;

    const fetchUsageData = () => {
        const endDate = format(new Date(), "yyyy-MM-dd");

        const dailyStartDate = format(subDays(new Date(), 7), "yyyy-MM-dd");
        revenueService
            .getUsageDay({ startDate: dailyStartDate, endDate })
            .then((result) => {
                setUsageDailyData(mapUsageToGraphData(result));
            })
            .catch((error) => {
                console.error("Error fetching usage day:", error);
            });

        const weeklyStartDate = format(subDays(new Date(), 49), "yyyy-MM-dd");
        revenueService
            .getUsageWeek({ startDate: weeklyStartDate, endDate })
            .then((result) => {
                setUsageWeeklyData(mapUsageToGraphData(result));
            })
            .catch((error) => {
                console.error("Error fetching usage week:", error);
            });

        const monthlyStartDate = format(subMonths(new Date(), 7), "yyyy-MM-dd");
        revenueService
            .getUsageDay({ startDate: monthlyStartDate, endDate })
            .then((result) => {
                setUsageMonthlyData(mapUsageToGraphData(result));
            })
            .catch((error) => {
                console.error("Error fetching usage monthly:", error);
            });
    };

    const fetchRevenueData = () => {
        const endDate = format(new Date(), "yyyy-MM-dd");

        const dailyStartDate = format(subDays(new Date(), 7), "yyyy-MM-dd");
        revenueService
            .getRevenueDay({ startDate: dailyStartDate, endDate })
            .then((result) => {
                setRevenueDailyData(mapRevenueToGraphData(result));
            })
            .catch((error) => {
                console.error("Error fetching revenue day:", error);
            });

        const weeklyStartDate = format(subDays(new Date(), 49), "yyyy-MM-dd");
        revenueService
            .getRevenueDay({ startDate: weeklyStartDate, endDate })
            .then((result) => {
                setRevenueWeeklyData(mapRevenueToGraphData(result));
            })
            .catch((error) => {
                console.error("Error fetching revenue weekly:", error);
            });

        const monthlyStartDate = format(subMonths(new Date(), 7), "yyyy-MM-dd");
        revenueService
            .getRevenueDay({ startDate: monthlyStartDate, endDate })
            .then((result) => {
                setRevenueMonthlyData(mapRevenueToGraphData(result));
            })
            .catch((error) => {
                console.error("Error fetching revenue monthly:", error);
            });
    };

    useEffect(() => {
        fetchUsageData();
        fetchRevenueData();
    }, []);

    const recentDeviceUsageCard = (
        <CustomGraphCard
            dailyData={usageDailyData}
            weeklyData={usageWeeklyData}
            monthlyData={usageMonthlyData}
            title={Translate("dashboard.recentDeviceUsage")}
            mainText={totalTransactions}
            subText={Translate("common.totalTransactions")}
            isBarChart={true}
            onDateRangeChange={setUsageTab}
        />
    );

    const totalRevenueCard = (
        <CustomGraphCard
            dailyData={revenueDailyData}
            weeklyData={revenueWeeklyData}
            monthlyData={revenueMonthlyData}
            title={Translate("dashboard.totalRevenue")}
            mainText={totalRevenue}
            onDateRangeChange={setRevenueTab}
        />
    );

    if (width <= 1400) {
        return (
            <>
                {recentDeviceUsageCard}
                <SizedBox height={"16px"} />
                {totalRevenueCard}
            </>
        );
    } else {
        return (
            <>
                <WebGraphGrid>
                    {recentDeviceUsageCard}
                    {totalRevenueCard}
                </WebGraphGrid>
            </>
        );
    }
}

export default DashboardGraphArea;
